import { Controller } from "@hotwired/stimulus"

// Main coordinator controller - manages devices array and overall state
export default class extends Controller {
  static targets = ["deviceContainer", "counter", "existingCount", "newCount"]
  static values = { 
    projectId: String, 
    floorPlanId: String,
    centerX: Number,
    centerY: Number,
    editingDeviceId: String,
    allDevices: Array
  }

  connect() {
    
    // Register controller instance for cross-controller access
    this.element.devicePlacementController = this
    
    // Initialize single source of truth for device state
    this.devices = new Map() // Use Map for O(1) lookups
    this.originalDeviceStates = new Map() // Track original states for change detection
    this.deviceIdCounter = 1
    this.selectedDeviceId = null
    this.mode = 'add' // 'add' or 'edit'
    this.hasChanges = false // Track if any changes have been made
    
    // Initialize center position for new devices
    this.centerX = this.centerXValue || 400
    this.centerY = this.centerYValue || 300
    
    // Setup event listeners for cross-controller communication
    this.setupEventListeners()
    
    // Delay loading existing devices to ensure other controllers are ready
    setTimeout(() => {
      this.loadExistingDevices()
      
      // Handle initial edit mode if editing specific device
      if (this.hasEditingDeviceIdValue && this.editingDeviceIdValue) {
        const deviceKey = `existing_${this.editingDeviceIdValue}`
        if (this.devices.has(deviceKey)) {
          this.selectDevice(deviceKey)
        }
      }
    }, 100)
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('device:icon:iconClicked', this.handleDeviceIconClick)  // Fixed: iconClicked not clicked
    document.removeEventListener('device:icon:canvasClicked', this.handleCanvasClick)
    document.removeEventListener('device:icon:iconDragged', this.handleDeviceIconDrag)  // Fixed: iconDragged not dragged
    document.removeEventListener('device:form:changed', this.handleFormChange)
    document.removeEventListener('device:form:addNew', this.handleAddNew)
    document.removeEventListener('device:form:clear', this.handleClear)
    document.removeEventListener('device:form:save', this.handleSave)
  }

  setupEventListeners() {
    // Handle events from device-icon-controller
    this.handleDeviceIconClick = (event) => {
      const { deviceId } = event.detail
      this.selectDevice(deviceId)
    }

    this.handleCanvasClick = (event) => {
      this.switchToAddMode()
    }
    
    this.handleDeviceIconDrag = (event) => {
      const { deviceId, positionX, positionY } = event.detail
      this.updateDevicePosition(deviceId, positionX, positionY)
    }
    
    // Handle events from device-form-controller
    this.handleFormChange = (event) => {
      if (this.selectedDeviceId && event.detail) {
        this.updateDevice(this.selectedDeviceId, event.detail)
      }
    }
    
    this.handleAddNew = (event) => {
      this.addNewDevice(event.detail)
    }
    
    this.handleClear = (event) => {
      if (this.selectedDeviceId) {
        this.clearDevice(this.selectedDeviceId)
      }
    }
    
    this.handleSave = (event) => {
      this.saveDevices()
    }
    
    // Validation failure handling is no longer needed since we use pre-validation
    
    // Handle mode change approval from form controller
    this.handleModeChangeApproved = (event) => {
      const { mode } = event.detail
      this.performModeChange(mode)
    }
    
    // Handle validation failure and restore selection
    this.handleValidationFailedRestoreSelection = (event) => {
      const { deviceId, invalidFields } = event.detail
      
      // Restore the selection in PlacementController
      if (deviceId && this.devices.has(deviceId)) {
        this.selectedDeviceId = deviceId
        this.mode = 'edit'
        
        // Notify Icon Controller to restore highlight, but don't update form
        this.dispatch('deviceSelected', { 
          deviceId: deviceId, 
          deviceData: this.devices.get(deviceId),
          skipFormUpdate: true  // Preserve current invalid form state
        })
      }
    }

    // Register event listeners
    document.addEventListener('device:icon:iconClicked', this.handleDeviceIconClick)
    document.addEventListener('device:icon:canvasClicked', this.handleCanvasClick)
    document.addEventListener('device:icon:iconDragged', this.handleDeviceIconDrag)
    document.addEventListener('device:form:changed', this.handleFormChange)
    document.addEventListener('device:form:addNew', this.handleAddNew)
    document.addEventListener('device:form:clear', this.handleClear)
    document.addEventListener('device:form:save', this.handleSave)
    document.addEventListener('device:form:modeChangeApproved', this.handleModeChangeApproved)
    document.addEventListener('device:form:validationFailedRestoreSelection', this.handleValidationFailedRestoreSelection)
  }

  loadExistingDevices() {
    if (!this.hasAllDevicesValue) {
      return
    }
    
    this.allDevicesValue.forEach(device => {
      const deviceData = {
        id: `existing_${device.id}`,
        originalId: device.id,
        deviceTypeId: device.deviceTypeId,
        deviceModelId: device.deviceModelId,
        placement: device.placement,
        powerSupplyId: device.powerSupplyId,
        powerPercentage: device.powerPercentage,
        positionX: device.positionX,
        positionY: device.positionY,
        isExisting: true,
        markedForDeletion: false
      }
      
      // Store original state for change detection
      const originalState = {
        deviceTypeId: device.deviceTypeId,
        deviceModelId: device.deviceModelId,
        placement: device.placement,
        powerSupplyId: device.powerSupplyId,
        powerPercentage: device.powerPercentage,
        positionX: device.positionX,
        positionY: device.positionY
      }
      
      this.devices.set(deviceData.id, deviceData)
      this.originalDeviceStates.set(deviceData.id, originalState)
      this.createDeviceIcon(deviceData)
    })
    
    this.updateDeviceCounter()
  }

  addNewDevice(formData) {
    const deviceId = `temp_${this.deviceIdCounter++}`
    
    // Calculate random offset for new device position to avoid stacking
    const offsetRange = 50 // pixels
    const randomOffsetX = (Math.random() - 0.5) * offsetRange * 2
    const randomOffsetY = (Math.random() - 0.5) * offsetRange * 2
    
    // Create new device data with slightly offset positioning
    const deviceData = {
      id: deviceId,
      ...formData,
      positionX: this.centerX + randomOffsetX,
      positionY: this.centerY + randomOffsetY,
      isExisting: false,
      markedForDeletion: false
    }
    
    // Add to internal state
    this.devices.set(deviceId, deviceData)
    
    // Create visual icon
    this.createDeviceIcon(deviceData)
    
    // Stay in add mode for continuous adding (don't select the new device)
    // User can click on the device icon if they want to edit it
    // this.selectDevice(deviceId) // REMOVED - stay in add mode
    
    // Update device counter display
    this.updateDeviceCounter()
    
    // Ensure we are in add mode after adding (clears previous selection if any)
    if (this.mode !== 'add') {
      this.performModeChange('add')
    }
    
    // Check for changes to notify confirm controller
    this.checkForChanges()
  }

  updateDevice(deviceId, updates) {
    const device = this.devices.get(deviceId)
    if (!device) {
      return
    }
    
    // Store old values for proper change detection
    const oldValues = {
      deviceTypeId: device.deviceTypeId,
      deviceModelId: device.deviceModelId,
      placement: device.placement,
      powerSupplyId: device.powerSupplyId,
      powerPercentage: device.powerPercentage
    }
    
    // Apply updates to device (normalize types for consistency)
    Object.keys(updates).forEach(key => {
      const newValue = updates[key]
      // Normalize deviceTypeId and deviceModelId to consistent types
      if (key === 'deviceTypeId' || key === 'deviceModelId') {
        device[key] = newValue ? String(newValue) : newValue
      } else {
        device[key] = newValue
      }
    })
    
    // Log actual changes detected
    const actualChanges = {}
    Object.keys(oldValues).forEach(key => {
      if (oldValues[key] !== device[key]) {
        actualChanges[key] = { from: oldValues[key], to: device[key] }
      }
    })
    
    // Check if device type changed (affects icon) - use normalized comparison
    const deviceTypeChanged = String(oldValues.deviceTypeId) !== String(device.deviceTypeId)
    if (deviceTypeChanged) {
    }
    
    // Notify icon controller to update icon (always notify, let icon controller decide)
    this.dispatch('deviceUpdated', { deviceId, deviceData: device })
    
    // Check for changes after device update (only if there were actual changes)
    if (Object.keys(actualChanges).length > 0) {
      this.checkForChanges()
    } else {
    }
  }

  updateDevicePosition(deviceId, positionX, positionY) {
    const device = this.devices.get(deviceId)
    if (!device) {
      return
    }
    
    device.positionX = positionX
    device.positionY = positionY
    
    if (this.selectedDeviceId === deviceId) {
      this.dispatch('devicePositionUpdated', { positionX, positionY })
    }
    
    this.checkForChanges()
  }

  clearDevice(deviceId) {
    const device = this.devices.get(deviceId)
    if (!device) return
    
    // ✨ Store the cleared device's config for reuse in add mode
    const clearedDeviceConfig = { ...device }
    delete clearedDeviceConfig.id
    delete clearedDeviceConfig.originalId
    delete clearedDeviceConfig.isExisting
    delete clearedDeviceConfig.markedForDeletion
    delete clearedDeviceConfig.positionX
    delete clearedDeviceConfig.positionY
    
    if (device.isExisting) {
      // Mark existing device for deletion
      device.markedForDeletion = true
      this.dispatch('deviceMarkedForDeletion', { deviceId })
    } else {
      // Remove temporary device completely
      this.devices.delete(deviceId)
      this.dispatch('deviceRemoved', { deviceId })
    }
    
    // Clear selection and switch to add mode with restored state
    this.selectedDeviceId = null
    this.mode = 'add'
    this.dispatch('modeChanged', { mode: 'add', restoreState: clearedDeviceConfig })
    this.updateDeviceCounter()
    
    // Check for changes after device clear
    this.checkForChanges()
  }

  selectDevice(deviceId) {
    const device = this.devices.get(deviceId)
    if (!device || device.markedForDeletion) {
      return
    }
    
    // 🚨 PRE-VALIDATION CHECK: Before changing any state, check if we can leave current device
    if (this.mode === 'edit' && this.selectedDeviceId && this.selectedDeviceId !== deviceId) {
      
      // Check validation synchronously
      if (!this.canLeaveCurrentDevice()) {
        return // Block device selection
      }
      
    }
    
    // Update selection state (only reached if validation passes)
    this.selectedDeviceId = deviceId
    this.mode = 'edit'
    
    // Notify other controllers
    this.dispatch('deviceSelected', { deviceId, deviceData: device })
    this.dispatch('modeChanged', { mode: 'edit' })
  }

  // Synchronous validation check method
  canLeaveCurrentDevice() {
    // Try direct access to form controller instance
    const formControllerElement = document.querySelector('[data-controller*="device-form"]')
    if (formControllerElement && formControllerElement.deviceFormController) {
      return formControllerElement.deviceFormController.canSafelyLeaveEditMode()
    }
    
    console.warn('Cannot access FormController for validation, allowing switch')
    return true
  }

  switchToAddMode() {
    // If we're currently in edit mode, we need to check if we can safely leave
    if (this.mode === 'edit') {
      // First attempt to change mode - form controller will validate
      this.dispatch('attemptModeChange', { 
        newMode: 'add',
        currentMode: this.mode,
        currentDeviceId: this.selectedDeviceId
      })
      // The actual mode change will happen in response to form controller's decision
    } else {
      // Already in add mode or no validation needed
      this.performModeChange('add')
    }
  }
  
  performModeChange(mode) {
    // Only change mode if it's actually different
    const previousMode = this.mode
    const previousSelectedDeviceId = this.selectedDeviceId
    
    // Clear current selection and change mode
    this.selectedDeviceId = null
    this.mode = mode
    
    // Only notify other controllers if mode actually changed
    if (previousMode !== mode) {
      this.dispatch('modeChanged', { 
        mode: mode, 
        previousMode: previousMode,
        previousSelectedDeviceId: previousSelectedDeviceId
      })
    } else {
    }
  }

  createDeviceIcon(deviceData) {
    // Dispatch event to create icon
    this.dispatch('createIcon', { 
      deviceId: deviceData.id,
      deviceData,
      container: this.hasDeviceContainerTarget ? this.deviceContainerTarget : null
    })
  }

  // Check if a device has actually changed compared to its original state
  hasDeviceChanged(device) {
    if (!device.isExisting) {
      // New devices are always considered "changed" (need to be created)
      return true
    }
    
    const originalState = this.originalDeviceStates.get(device.id)
    if (!originalState) {
      // If no original state, assume changed
      return true
    }
    
    // Compare current state with original state (with type normalization)
    const currentState = {
      deviceTypeId: device.deviceTypeId,
      deviceModelId: device.deviceModelId,
      placement: device.placement,
      powerSupplyId: device.powerSupplyId,
      powerPercentage: device.powerPercentage,
      positionX: device.positionX,
      positionY: device.positionY
    }
    
    // Check each field for changes with proper type handling
    for (const [key, currentValue] of Object.entries(currentState)) {
      const originalValue = originalState[key]
      
      // Normalize values for comparison (especially for ID fields that might be strings vs numbers)
      let normalizedCurrent = currentValue
      let normalizedOriginal = originalValue
      
      if (key === 'deviceTypeId' || key === 'deviceModelId' || key === 'powerSupplyId') {
        // Normalize ID fields to strings for comparison
        normalizedCurrent = currentValue ? String(currentValue) : currentValue
        normalizedOriginal = originalValue ? String(originalValue) : originalValue
      }
      
      // Handle null/undefined comparisons
      if (normalizedCurrent !== normalizedOriginal) {
        return true
      }
    }
    
    return false
  }

  updateDeviceCounter() {
    if (!this.hasCounterTarget) return
    
    const activeDevices = Array.from(this.devices.values())
      .filter(d => !d.markedForDeletion)
    
    // Count existing vs new devices separately
    const existingDevices = activeDevices.filter(d => d.isExisting)
    const newDevices = activeDevices.filter(d => !d.isExisting)
    
    const existingCount = existingDevices.length
    const newCount = newDevices.length
    const totalCount = existingCount + newCount
    
    // Update the separate counters
    if (this.hasExistingCountTarget) {
      this.existingCountTarget.textContent = existingCount
    }
    if (this.hasNewCountTarget) {
      this.newCountTarget.textContent = newCount
    }
    
    // Show/hide counter based on total device count
    this.counterTarget.style.display = totalCount > 0 ? 'flex' : 'none'
    
  }

  saveDevices() {
    // Log all device positions before saving
    this.devices.forEach((device, deviceId) => {
      const status = device.markedForDeletion ? '❌ marked for deletion' : 
                     device.isExisting ? '🔄 existing to update' : '✨ new to create'
    })
    
    // Validate all devices before saving
    const validationErrors = []
    this.devices.forEach((device, deviceId) => {
      if (!device.markedForDeletion) {
        // Validate required fields for active devices
        if (!device.deviceTypeId) {
          validationErrors.push(`Device ${deviceId}: Device Type is required`)
        }
        if (!device.deviceModelId) {
          validationErrors.push(`Device ${deviceId}: Device Model is required`)
        }
        
        // For Access Point (deviceTypeId === '1'), also validate placement and power supply
        if (device.deviceTypeId === '1' || device.deviceTypeId === 1) {
          if (!device.placement) {
            validationErrors.push(`Device ${deviceId}: Placement is required for Access Point`)
          }
          if (!device.powerSupplyId) {
            validationErrors.push(`Device ${deviceId}: Power Supply is required for Access Point`)
          }
        }
      }
    })
    
    // If there are validation errors, show them and prevent submission
    if (validationErrors.length > 0) {
      console.error('Validation errors:', validationErrors)
      
      // If in edit mode with a selected device, trigger form validation to show errors
      if (this.selectedDeviceId) {
        const formController = document.querySelector('[data-controller*="device-form"]')?.deviceFormController
        if (formController) {
          formController.validateForm('edit')
        }
      }
      
      // Dispatch validation failed event
      this.dispatch('validationFailed', { errors: validationErrors })
      return
    }
    
    // Separate devices into different operations
    const devicesToCreate = []
    const devicesToUpdate = []
    const devicesToDelete = []
    
    // Check if we have any devices to save
    if (this.devices.size === 0) {
      // No devices added - show validation error in form
      this.dispatch('noDevicesToSave')
      return
    }
    
    this.devices.forEach((device, deviceId) => {
      
      if (device.markedForDeletion) {
        // Only existing devices can be deleted
        if (device.isExisting) {
          devicesToDelete.push(device.originalId)
        } else {
        }
        // New devices that are marked for deletion are simply not saved
      } else {
        if (!device.isExisting) {
          // New device to create - always needs to be created
          const deviceToSave = {
            device_type_id: device.deviceTypeId,
            device_model_id: device.deviceModelId,
            placement: device.placement,
            power_supply_id: device.powerSupplyId,
            power_percentage: device.powerPercentage,
            position_x: device.positionX,
            position_y: device.positionY
          }
          devicesToCreate.push(deviceToSave)
        } else {
          // Existing device - only update if actually changed
          const hasChanged = this.hasDeviceChanged(device)
          if (hasChanged) {
            const deviceToSave = {
              id: device.originalId,
              device_type_id: device.deviceTypeId,
              device_model_id: device.deviceModelId,
              placement: device.placement,
              power_supply_id: device.powerSupplyId,
              power_percentage: device.powerPercentage,
              position_x: device.positionX,
              position_y: device.positionY
            }
            devicesToUpdate.push(deviceToSave)
          } else {
          }
        }
      }
    })
    
    // Summary before submission
    // Submit all operations atomically in a single request
    if (devicesToCreate.length > 0 || devicesToUpdate.length > 0 || devicesToDelete.length > 0) {
      this.submitBatchCommit(devicesToCreate, devicesToUpdate, devicesToDelete)
    } else {
      // Reset the hasChanges flag and notify confirm controller
      this.hasChanges = false
      const formResetEvent = new CustomEvent('formReset', {
        detail: { reset: true }
      })
      document.dispatchEvent(formResetEvent)
      
      // No actual changes to save - show user-friendly message
      const currentUrl = window.location.pathname
      const floorPlanUrl = currentUrl.replace(/\/devices.*/, '')
      window.location.href = floorPlanUrl + '?notice=' + encodeURIComponent('No changes were made')
    }
  }

  submitBatchCommit(devicesToCreate, devicesToUpdate, devicesToDelete) {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/devices/batch_commit`
    
    // Add CSRF token (if available)
    const csrfTokenElement = document.querySelector('meta[name="csrf-token"]')
    if (!csrfTokenElement) {
    } else {
      const csrfToken = csrfTokenElement.content
      
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }
    
    // Add devices to create
    devicesToCreate.forEach((device, index) => {
      Object.entries(device).forEach(([key, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = `creates[${index}][${key}]`
        input.value = value || ''
        form.appendChild(input)
      })
    })

    // Add devices to update
    devicesToUpdate.forEach((device, index) => {
      Object.entries(device).forEach(([key, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = `updates[${index}][${key}]`
        input.value = value || ''
        form.appendChild(input)
      })
    })
    
    // Add devices to delete
    devicesToDelete.forEach((deviceId, index) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `deletions[${index}]`
      input.value = deviceId
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  // Check if any device has changed and update hasChanges flag
  checkForChanges() {
    let hasAnyChanges = false
    
    // Check if there are any new devices
    const newDevices = Array.from(this.devices.values()).filter(d => !d.isExisting && !d.markedForDeletion)
    if (newDevices.length > 0) {
      hasAnyChanges = true
    }
    
    // Check if any existing devices have been deleted
    const deletedDevices = Array.from(this.devices.values()).filter(d => d.isExisting && d.markedForDeletion)
    if (deletedDevices.length > 0) {
      hasAnyChanges = true
    }
    
    // Check if any existing devices have been modified
    const modifiedDevices = Array.from(this.devices.values()).filter(d => d.isExisting && !d.markedForDeletion && this.hasDeviceChanged(d))
    if (modifiedDevices.length > 0) {
      hasAnyChanges = true
    }
    
    // Update hasChanges flag and notify confirm controller if changed
    if (hasAnyChanges !== this.hasChanges) {
      this.hasChanges = hasAnyChanges
      
      if (this.hasChanges) {
        // Dispatch event that confirm controller listens to
        const formChangedEvent = new CustomEvent('formChanged', {
          detail: { changed: true }
        })
        document.dispatchEvent(formChangedEvent)
      }
    }
    
    return this.hasChanges
  }

  // Helper method to dispatch custom events
  dispatch(eventName, detail = {}) {
    const event = new CustomEvent(`device:placement:${eventName}`, { detail })
    document.dispatchEvent(event)
  }
}
